'use client'
import { useState } from 'react'

/**
 * Calm, muted "keep this link" safety net for the post-checkout confirmation
 * screen. The durable /booking/[token] page is the only refresh-survivable entry
 * to the post-checkout flow; this surfaces it on-screen so a reload/tab-close
 * doesn't strand the customer (the confirmation email is async + best-effort).
 *
 * Not a banner: secondary/muted tokens, no alarm colour, sits under a thin
 * separator below the flow runner. The href is a RELATIVE path (storefront origin
 * already is the tenant host) so nothing sensitive lands in a query string. Copy
 * mirrors CopyHexButton's clipboard-with-textarea-fallback.
 */
export function BookingLinkKeepsake({ token }: { token: string }) {
  const [copied, setCopied] = useState(false)
  const path = `/booking/${token}`

  async function copy() {
    const url = typeof window !== 'undefined' ? `${window.location.origin}${path}` : path
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url)
      } else {
        const ta = document.createElement('textarea')
        ta.value = url
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        ta.remove()
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      /* clipboard blocked — no false "Copied" */
    }
  }

  return (
    <div className="border-t border-[var(--color-border)] pt-4">
      <p className="text-sm text-[var(--color-text-secondary)]">
        Keep this link to pick up where you left off.
      </p>
      <div className="mt-2 flex items-center gap-2">
        <a
          href={path}
          className="min-w-0 flex-1 truncate font-mono text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
        >
          {path}
        </a>
        <button
          type="button"
          onClick={copy}
          className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-elevated,#fff)]"
        >
          <span aria-hidden>{copied ? '✓' : '⧉'}</span>
          {copied ? 'Copied' : 'Copy'}
          <span aria-live="polite" className="sr-only">{copied ? 'Link copied' : ''}</span>
        </button>
      </div>
    </div>
  )
}
